import { type Event } from '@kalos/kalos-rpc';
import { Button } from '@kalos/ui';
import {
  CheckCircledIcon,
  ClockIcon,
  ExternalLinkIcon,
  GearIcon,
  ReaderIcon,
  RocketIcon,
} from '@radix-ui/react-icons';
import { type FC } from 'react';

import { useServiceItemGetQuery } from '../../../../hooks/react-query/useServiceItemQuery';
import { getSystemReadingsTypeLabel } from '../../Equipment/systemReadingsTypeOptions';
import { useJobHistoryFacts } from './useJobHistoryFacts';
import { laborHoursLabel, VISIT_DETAIL_SECTIONS, VISIT_FACT_LABELS } from './visitDetailFields';

type JobHistoryDetailProps = {
  event: Event;
  filterServiceItemId?: number;
  /** Text scale for the detail content. 'lg' matches the scheduler Review step. */
  size?: 'default' | 'compact';
};

const InfoRow = ({
  icon,
  title,
  children,
  size = 'default',
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  size?: 'default' | 'compact';
}) => (
  <div>
    <div className="mb-1 flex items-center space-x-2">
      {icon}
      <h4 className={`font-semibold text-gray-700 ${size === 'compact' ? 'text-xs' : ''}`}>
        {title}
      </h4>
    </div>
    <div className={`pl-6 text-gray-600 ${size === 'compact' ? 'text-xs' : 'text-sm'}`}>
      {children}
    </div>
  </div>
);

const ServiceItemDisplay = ({ serviceItemId }: { serviceItemId: number }) => {
  const {
    data: serviceItem,
    isLoading,
    error,
  } = useServiceItemGetQuery({
    id: serviceItemId,
    enabled: !!serviceItemId,
  });

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="mb-2 h-4 w-3/4 rounded bg-gray-200"></div>
        <div className="h-3 w-1/2 rounded bg-gray-200"></div>
      </div>
    );
  }

  if (error || !serviceItem) {
    return <span className="text-red-500">Error loading service item</span>;
  }

  const typeLabel = getSystemReadingsTypeLabel(serviceItem.systemReadingsTypeId || 0);
  const nameLabel = serviceItem.type?.trim() || '';

  return (
    <div className="mb-3 rounded border bg-white p-3">
      <div className="mb-1 text-base font-semibold text-gray-900">
        {typeLabel || 'Type not selected'}
      </div>
      <div className="mb-2 text-sm text-gray-600">{nameLabel || 'No name'}</div>
      <div className="text-sm text-gray-600">
        {serviceItem.brand && serviceItem.model && (
          <div>
            Brand: {serviceItem.brand} | Model: {serviceItem.model}
          </div>
        )}
        {serviceItem.serial && <div>Serial: {serviceItem.serial}</div>}
        {serviceItem.startDate && <div>Start Date: {serviceItem.startDate}</div>}
      </div>
    </div>
  );
};

export const JobHistoryDetail: FC<JobHistoryDetailProps> = ({
  event,
  filterServiceItemId,
  size = 'default',
}) => {
  const { laborHours, technicians, serviceItemIds } = useJobHistoryFacts(
    event,
    filterServiceItemId,
  );

  const handleViewFullJob = () => {
    const url = `/jobs/${event.id}`;
    window.open(url, '_blank');
  };

  const partsUsed = event.materialUsed || VISIT_DETAIL_SECTIONS.partsUsed.empty;

  return (
    <div className="border-t bg-gray-50 p-4">
      <div className="mb-4 grid grid-cols-2 gap-x-8 gap-y-4">
        <InfoRow
          icon={<ReaderIcon className="h-4 w-4" />}
          title={VISIT_DETAIL_SECTIONS.serviceNeeded.title}
          size={size}
        >
          {event.description || VISIT_DETAIL_SECTIONS.serviceNeeded.empty}
        </InfoRow>
        <InfoRow
          icon={<CheckCircledIcon className="h-4 w-4" />}
          title={VISIT_DETAIL_SECTIONS.jobNotes.title}
          size={size}
        >
          <p className="whitespace-pre-wrap">
            {event.logNotes || VISIT_DETAIL_SECTIONS.jobNotes.empty}
          </p>
        </InfoRow>
        <InfoRow icon={<GearIcon className="h-4 w-4" />} title="Job Type/Subtype" size={size}>
          <p className="font-medium">
            {event.jobType} - {event.jobSubtype}
          </p>
        </InfoRow>
        <InfoRow icon={<ClockIcon className="h-4 w-4" />} title="Job Details" size={size}>
          <p>
            {VISIT_FACT_LABELS.technician}: {technicians}
          </p>
          <p>
            {VISIT_FACT_LABELS.labor}: {laborHoursLabel(laborHours)}
          </p>
        </InfoRow>
        <InfoRow
          icon={<RocketIcon className="h-4 w-4" />}
          title={VISIT_DETAIL_SECTIONS.partsUsed.title}
          size={size}
        >
          {partsUsed}
        </InfoRow>
      </div>
      <div className="my-4 border-b" />
      <InfoRow
        icon={<GearIcon className="h-4 w-4" />}
        title={VISIT_DETAIL_SECTIONS.equipment.title}
        size={size}
      >
        {serviceItemIds.length > 0 ? (
          <div>
            {serviceItemIds.map((serviceItemId) => (
              <ServiceItemDisplay key={serviceItemId} serviceItemId={serviceItemId} />
            ))}
          </div>
        ) : (
          VISIT_DETAIL_SECTIONS.equipment.empty
        )}
      </InfoRow>
      <div className="my-4 border-b" />
      <InfoRow
        icon={<GearIcon className="h-4 w-4" />}
        title={VISIT_DETAIL_SECTIONS.notes.title}
        size={size}
      >
        {event.notes || VISIT_DETAIL_SECTIONS.notes.empty}
      </InfoRow>
      <div className="mt-4 flex justify-end">
        <Button onClick={handleViewFullJob} variant="outline">
          View Full Job Details
          <ExternalLinkIcon className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
